import { useEffect, useState, useMemo, type FC } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Printer, RotateCcw, Search } from 'lucide';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { createFilterOptions } from '@mui/material/Autocomplete';

// common
import Icon from 'common/components/Icon';
import SchemaForm from 'common/SchemaForm';
import { LINKING_STATUS } from 'common/constants';
import { HookPaginationProps } from 'common/hook/usePaginationHook';
import { apiClient } from 'api/apiClient';

// screen
import { SupplierFilterModel } from 'modules/suppliers/types';
import useGetListClients from 'modules/account/services/useGetListClients';
import { Client } from 'modules/account/types';

interface FilterProps {
  hookPagination: HookPaginationProps<SupplierFilterModel>;
  selectedClient: Client | null;
  onSetCustomer: (client: Client | null) => void;
  /** Khóa ô khách hàng (khách hàng / phụ huynh — trường cố định theo tài khoản). */
  disableClientSelect?: boolean;
  /**
   * Nút làm mới. Tài khoản nhiều trường: xóa trắng. Tài khoản một trường: tải lại,
   * không gỡ khách vì ô đã khóa.
   */
  onReset?: () => void;
}

const SEARCH_BUTTON_SX = {
  height: 40,
  px: 3,
  textTransform: 'none',
} as const;

const REFRESH_BUTTON_SX = {
  minWidth: 40,
  px: 1,
  flexShrink: 0,
} as const;

const FilterBox: FC<FilterProps> = (props) => {
  const { hookPagination, selectedClient, onSetCustomer, disableClientSelect, onReset } = props;
  const [filterdClients, setFilteredClients] = useState<Client[]>([]);

  const filterClientOptions = useMemo(
    () =>
      createFilterOptions<Client>({
        stringify: (option) => `${option?.name || ''} ${option?.operationCode || ''}`.trim(),
      }),
    []
  );

  const { setParams, filters } = hookPagination;

  // HOTFIX: xem chú thích ở SupplierListPage — tạm đổi type 2 -> 1 để dropdown khách hàng không rỗng.
  const { data } = useGetListClients({ type: 1 });

  useEffect(() => {
    if (!selectedClient && !data) {
      setFilteredClients([]);
      return;
    }

    if (!data) {
      setFilteredClients([]);
      return;
    }

    setFilteredClients(data);
  }, [data, selectedClient]);

  const schemaConfig = useMemo(
    () => ({
      fields: ({
        formProps: {
          intl: { formatMessage },
          isMobile,
        },
        methods: { setValue },
      }) => {
        return {
          customerOperationCode: {
            mode: 'auto-complete' as const,
            // Ô tên trường ăn phần rộng còn lại, giống thanh lọc Dashboard.
            propsWrapper: {
              mobile: 12,
              tablet: true,
              sx: { minWidth: { tablet: 240 } },
            },
            label: formatMessage({ id: 'supplier.customer' }),
            placeholder: formatMessage({ id: 'supplier.customer' }),
            rules: { required: true },
            readOnly: disableClientSelect,
            options: filterdClients,
            rawOptions: true,
            disableClearBtn: true,
            onChange: (client) => {
              onSetCustomer(client || null);
            },
            filterOptions: filterClientOptions,
            getOptionLabel: (option: any) =>
              (typeof option === 'string'
                ? option
                : option?.name || option?.operationCode || '') as any,
            isOptionEqualToValue: (option: any, value: any) =>
              // compare by operationCode when available, fall back to id
              option?.operationCode && value?.operationCode
                ? option.operationCode === value.operationCode
                : option?.id === value?.id,
          },
          status: {
            mode: 'select' as const,
            label: formatMessage({ id: 'supplier.supplierStatus' }),
            options: LINKING_STATUS,
            propsWrapper: {
              mobile: 12,
              tablet: 'auto',
              sx: { width: { tablet: 220 }, minWidth: { tablet: 180 }, flexShrink: 0 },
            },
            disableClearBtn: true,
            rules: { required: true },
            onChange: (status: SupplierFilterModel['status']) => {
              const code = selectedClient?.operationCode || filters.customerOperationCode;
              if (!code || !status) return;
              setParams({ customerOperationCode: code, status });
            },
          },
          submit: {
            mode: 'button' as const,
            type: 'submit' as const,
            variant: 'contained' as const,
            color: 'primary' as const,
            disableElevation: true,
            sx: SEARCH_BUTTON_SX,
            startIcon: <Icon icon={Search} size={16} />,
            children: !isMobile && formatMessage({ id: 'search' }),
            propsWrapper: {
              mobile: isMobile ? 2 : undefined,
              height: isMobile ? undefined : '40px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              marginTop: isMobile ? '0px' : '18px',
              marginBottom: '0px',
            },
          },
          actions: {
            mode: 'node' as const,
            propsWrapper: {
              mobile: 12,
              tablet: 'auto',
              marginTop: isMobile ? '0px' : '18px',
            },
            render: () => (
              <Box sx={{ display: 'flex', gap: 1, height: '40px', alignItems: 'stretch' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  aria-label="Làm mới"
                  sx={REFRESH_BUTTON_SX}
                  onClick={() => {
                    if (!disableClientSelect) {
                      setValue('customerOperationCode', null as any);
                      setValue('status', 'available' as any);
                    }
                    onReset?.();
                  }}
                >
                  <Icon icon={RotateCcw} />
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Icon icon={Printer} size={16} />}
                  disabled={!selectedClient?.operationCode}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={async () => {
                    const customerOperationCode = selectedClient?.operationCode;
                    if (!customerOperationCode) return;
                    try {
                      const url = await apiClient().SupplierProfileController_getA3Preview({
                        params: { customerOperationCode },
                      });
                      if (url) window.open(url, '_blank', 'noopener,noreferrer');
                    } catch {
                      enqueueSnackbar({
                        message: 'Không lấy được URL A3 Preview',
                        variant: 'error',
                      });
                    }
                  }}
                >
                  {formatMessage({ id: 'supplier.a3Preview' })}
                </Button>
              </Box>
            ),
          },
        };
      },
    }),
    [
      filterdClients,
      onSetCustomer,
      filterClientOptions,
      selectedClient,
      disableClientSelect,
      filters.customerOperationCode,
      setParams,
      onReset,
    ]
  );

  const formDataValue = useMemo(() => {
    // Find the Client object that matches the stored operationCode
    const clientFromFilters = filters.customerOperationCode
      ? filterdClients.find((c) => c.operationCode === filters.customerOperationCode)
      : null;

    return {
      customerOperationCode: (selectedClient || clientFromFilters || null) as any,
      status: filters.status || 'available',
    };
  }, [filters.customerOperationCode, filters.status, selectedClient, filterdClients]);

  return (
    <SchemaForm<SupplierFilterModel>
      onSubmit={(val) => {
        setParams({
          customerOperationCode: val.customerOperationCode.operationCode,
          status: val.status,
        });
      }}
      formData={formDataValue}
      schema={schemaConfig}
    />
  );
};

export default FilterBox;
